import { isEqual } from 'lodash-es';
import i18next from 'i18next';
import { draft, pendingMatches, awaitingConfirmation, scheduled, pastStatuses } from '@/constants';
import { toEpoch, epochToTime, epochToDate } from '@/utils/epoch';
import { required, minLength, maxLength, positiveInteger, dateNotInPast } from '@/utils/validation/validators';
import { afterStartTime, dateAndTimeInFuture, withinBusinessHours, withinRecommendedLeadTime } from '@/utils/validation/requestValidators';
import { requestFormClickNames } from '@/constants/requests';

/**
 * Flexibility only matters while a request is still being arranged, so it is hidden once the
 * request is scheduled or in a past status (completed, expired, canceled).
 * @param {string} [status] - Request status
 * @returns {boolean}
 */
export function showsFlexibility(status) {
    return ![scheduled, ...pastStatuses].includes(status);
}

/**
 * Whether a directory field (`date` or `time`) should be presented as flexible for the request.
 * @param {string} field - Directory column field
 * @param {{flexibleDate?: boolean, flexibleTime?: boolean, status?: string}} [flexibility]
 * @returns {boolean}
 */
export function isFlexibleField(field, { flexibleDate = false, flexibleTime = false, status } = {}) {
    const isFlexible = (field === 'date' && flexibleDate) || (field === 'time' && flexibleTime);
    return isFlexible && showsFlexibility(status);
}

/**
 * Appends a localized "(Flexible)" marker to a directory date or time value when the request
 * is flexible on that field and still being arranged. Other fields, non-flexible requests and
 * scheduled/past requests are returned unchanged.
 * @param {string} field - Directory column field (`date` or `time`)
 * @param {string} value - Formatted value for the column
 * @param {{flexibleDate?: boolean, flexibleTime?: boolean, status?: string}} [flexibility]
 */
export function withFlexibleLabel(field, value, flexibility = {}) {
    return isFlexibleField(field, flexibility) ? `${value} (${i18next.t('Flexible')})` : value;
}

/**
 * Decides the product tracking name for a step change in the request form, or `null` when
 * that step shouldn't be tracked (e.g. the final step, which is already tracked on publish).
 * @param {number} step
 * @param {boolean} isFinalStep
 * @returns {string|null}
 */
export function generateStepTrackingMessage(step, isFinalStep, backward = false) {
    if (typeof step !== 'number' || isFinalStep) return null;
    if (step < 1) return null;

    if (backward) return requestFormClickNames.STEP_BACKWARD(step);
    return requestFormClickNames.STEP_FORWARD(step);
}

/**
 * Decides whether a new request goes out to the whole volunteer pool (open) or to one specific volunteer (direct).
 * An explicitly chosen volunteer wins over the volunteer who owns the opportunity the educator came from.
 * @param {string} [employeeId] - Volunteer the educator explicitly picked.
 * @param {string} [opportunityEmployeeId] - Volunteer who owns the CareerShare opportunity, if one was loaded.
 * @param {object} [options]
 * @param {boolean} [options.opportunityMissing] - A CareerShare was asked for but could not be loaded. The target can't be
 * trusted, so the request must not silently fall back to open.
 * @returns {{mode: 'open'} | {mode: 'direct', employeeId: string} | {mode: 'unresolved'}}
 */
export function resolveRequestTarget(employeeId, opportunityEmployeeId, { opportunityMissing = false } = {}) {
    if (opportunityMissing) return { mode: 'unresolved' };

    const volunteerId = employeeId || opportunityEmployeeId;
    return volunteerId ? { mode: 'direct', employeeId: volunteerId } : { mode: 'open' };
}

/**
 * Narrows requests to the ones a volunteer may see. An awaiting-confirmation request is shown only when it is
 * unassigned or assigned to this volunteer; every other status is visible. Matches the request detail access rule.
 * @param {Array<{status?: string, employeeId?: string|null}>} [requests]
 * @param {string} employeeId - The volunteer viewing the list.
 * @returns {Array} A new array; the input is not mutated.
 */
export function filterVisibleVolunteerRequests(requests, employeeId) {
    return (requests ?? []).filter((request) => {
        if (request.status !== awaitingConfirmation) return true;
        return !request.employeeId || request.employeeId === employeeId;
    });
}

export function normalizeRequestResponse(apiRequest) {
    if (!apiRequest) return createRequestFields();
    const { startDateTime, endDateTime, subjects = [], primarySubject } = apiRequest;

    return createRequestFields({
        id: apiRequest.requestId,
        title: apiRequest.title,
        description: apiRequest.description,
        date: startDateTime ? epochToDate(startDateTime) : '',
        flexibleDate: apiRequest.flexibleDate ?? false,
        timeStart: startDateTime ? epochToTime(startDateTime) : '',
        timeEnd: endDateTime ? epochToTime(endDateTime) : '',
        flexibleTime: apiRequest.flexibleTime ?? false,
        primarySubject: primarySubject ?? '',
        subjects: subjects.filter((subject) => subject !== primarySubject),
        gradeLevel: String(apiRequest.gradeLevel) || null,
        classSize: Number(apiRequest.classSize) || 0,
        meetingLink: apiRequest.meeting_link ?? '',
        employeeId: apiRequest.employeeId ?? null,
        status: apiRequest.status ?? draft,
        isDirectRequest: apiRequest.isDirectRequest ?? false,
        className: apiRequest.className ?? '',

        // Type must ALWAYS be set to 2, will be removed from API at a later date
        type: '2',
    });
}

/**
 * Default/merged field values for a request form.
 * @param {object} [initial] - Starting field values, e.g. from `normalizeRequestResponse`.
 */
function createRequestFields(initial = {}) {
    return {
        id: null,
        title: '',
        description: '',
        date: '',
        flexibleDate: false,
        timeStart: '',
        timeEnd: '',
        flexibleTime: false,
        primarySubject: '',
        subjects: [],
        gradeLevel: '',
        classSize: '',
        meetingLink: '',
        employeeId: null,
        status: draft,
        ...initial,
    };
}

/**
 * Domain model for a request. Holds the business rules around denormalizing and
 * re-categorizing a request (direct/edit/reschedule/duplicate).
 * Field-level validation rules are defined by `getRequestValidationRules` below
 * and are run by the orchestrating composable via `useFormValidation`, not by this model.
 *
 * @param {object} fields - Typically `createRequestFields(...)` wrapped in a reactive object.
 * @param {object} educatorContext - The educator's user data (`mainStore.user.data`); `.id` and `.site` are read from it.
 * @param {string} [requestID] - The requestID of an existing request.
 * @param {object} [opts]
 * @param {boolean} [opts.isDuplicate] - Is a duplicate of an existing request.
 */
export function requestModel(fields, educatorContext, requestID, { isDuplicate = false } = {}) {
    let snapshotFromServer = null;
    const educatorID = educatorContext.id;

    function isEdit() {
        return !isDuplicate && (!!snapshotFromServer?.id || !!requestID);
    }

    function makeDirectRequest(employeeId) {
        fields.employeeId = employeeId;
        return fields;
    }

    function needsReschedule() {
        if (!isEdit()) return false;

        const datesHaveChanged = !isEqual(fields.date, snapshotFromServer.date);
        const startTimeChanged = fields.timeStart !== snapshotFromServer.timeStart;
        const endTimeChanged = fields.timeEnd !== snapshotFromServer.timeEnd;
        const flexibilityDateChanged = fields.flexibleDate !== snapshotFromServer.flexibleDate;
        const flexibilityTimeChanged = fields.flexibleTime !== snapshotFromServer.flexibleTime;
        return datesHaveChanged || startTimeChanged || endTimeChanged || flexibilityDateChanged || flexibilityTimeChanged;
    }

    function hasEditedChanges() {
        if (!isEdit()) return false;
        return Object.keys(fields).some((key) => !isEqual(fields[key], snapshotFromServer[key]));
    }

    function resolveStatus({ savingDraft }) {
        if (savingDraft) return draft;

        if (fields.status && fields.status !== draft) return fields.status;
        return fields.employeeId ? awaitingConfirmation : pendingMatches;
    }

    function canFetchAnExistingRequest() {
        if (!educatorID || typeof educatorID !== 'string') return false;
        if (!requestID || typeof requestID !== 'string') return false;
        return true;
    }

    /**
     * -> API payload shape.
     * @param {object} [opts]
     * @param {boolean} [opts.savingDraft] - Keep/assign draft status rather than submitting.
     */
    function denormalize({ savingDraft = false } = {}) {
        const generateSiteString = () => {
            const { site_name: siteName = '', site_city: siteCity = '', site_state: siteState = '' } = educatorContext?.site ?? {};
            if (!siteName && !siteCity && !siteState) return '';

            const siteLocationSlug = [siteCity, siteState].filter(Boolean).join(', ');
            return `${siteName}|||${siteLocationSlug}`;
        };

        const className = fields.className || generateSiteString();
        return {
            title: fields.title,
            description: fields.description,
            type: fields.type,
            primarySubject: fields.primarySubject,

            // API still needs us to specify primary subject in the subjects list, despite
            // us no longer allowing more than 1 subject per request.
            subjects: [fields.primarySubject],
            gradeLevel: String(fields.gradeLevel),
            classSize: parseInt(fields.classSize, 10),
            flexibleDate: fields.flexibleDate,
            flexibleTime: fields.flexibleTime,
            startDateTime: toEpoch(fields.date, fields.timeStart),
            endDateTime: toEpoch(fields.date, fields.timeEnd),
            meeting_link: fields.meetingLink,
            status: resolveStatus({ savingDraft }),
            className,
            ...(fields.employeeId ? { employeeId: fields.employeeId } : {}),
            educatorId: educatorID,
        };
    }

    function snapshotOriginalRequest(requestMetadata) {
        const requestFields = createRequestFields(normalizeRequestResponse(requestMetadata));

        snapshotFromServer = { ...requestFields };
        return snapshotFromServer;
    }

    function snapshotDuplicateRequest(requestMetadata) {
        snapshotFromServer = null;
        const requestFields = createRequestFields(normalizeRequestResponse(requestMetadata));

        return {
            ...requestFields,
            id: null,
            status: draft,
            employeeId: null,
            isDirectRequest: false,
            date: '',
            flexibleDate: false,
            timeStart: '',
            timeEnd: '',
            flexibleTime: false,
            meetingLink: '',
        };
    }

    /**
     * Fields a new request can borrow from the CareerShare opportunity the educator came from.
     * Only fills what the opportunity actually has; schedule, meeting and class details stay with the educator.
     * @param {object} [opportunity]
     * @returns {object} Partial form fields to merge over the defaults.
     */
    function snapshotOpportunityRequest(opportunity) {
        const { title, description, subjects } = opportunity || {};
        const [primarySubject, ...additionalSubjects] = Array.isArray(subjects) ? subjects : [];

        return {
            ...(title ? { title } : {}),
            ...(description ? { description } : {}),
            ...(primarySubject ? { primarySubject, subjects: additionalSubjects } : {}),
        };
    }

    return {
        fields,
        requestID,
        educatorID,
        isEdit,
        makeDirectRequest,
        snapshotOpportunityRequest,
        needsReschedule,
        hasEditedChanges,
        canFetchAnExistingRequest,
        snapshotDuplicateRequest,
        snapshotOriginalRequest,
        denormalize,
    };
}

/**
 * Business rules for what makes a request valid. Built per-model-instance (rather than as a
 * static map) because `timeEnd`/lead-time checks need to read sibling field values at validation time.
 * @param {object} fields - `requestModel(...).fields`
 */
export function getRequestValidationRules(fields) {
    // This value is thunk. So only when the afterStartTime(...) validator calls this function will we grab the latest snapshot of timeStart. Otherwise, passing fields.timeStart passed in directly will be a stale value that was computed at init of getRequestValiidationRules.
    const evalStartTimeCurrent = () => fields.timeStart;
    const evalCurrentDate = () => fields.date;

    // Reuses maxLength's length check, but with a softer, recommendation-style message
    // rather than the default "Maximum n characters allowed" wording.
    const maxLengthRequestSpecificMessage = (n) => (value) =>
        maxLength(n)(value) ? i18next.t("We wouldn't recommend a field over %(n)s characters long", { n }) : null;

    return {
        errorRules: {
            title: [required()],
            description: [required()],
            date: [required(), dateNotInPast(), dateAndTimeInFuture(evalStartTimeCurrent, evalCurrentDate)],
            timeStart: [required(), withinBusinessHours('07:00', '19:00'), dateAndTimeInFuture(evalStartTimeCurrent, evalCurrentDate)],
            timeEnd: [required(), afterStartTime(evalStartTimeCurrent), withinBusinessHours('07:00', '21:00')],
            primarySubject: [required()],
            gradeLevel: [required()],
            classSize: [required(), positiveInteger()],
            meetingLink: [required()],
        },
        warningRules: {
            title: [minLength(10), maxLengthRequestSpecificMessage(100)],
            date: [withinRecommendedLeadTime()],
        },
    };
}
